import { Injectable, signal } from '@angular/core'
import { isServer } from '@mos/client/shared/utils'

export interface AcquisitionData {
	utmSource: string | null
	utmMedium: string | null
	utmCampaign: string | null
	utmContent: string | null
	utmTerm: string | null
	fbclid: string | null
	gclid: string | null
	ttclid: string | null
	referrer: string | null
	// ISO date of the landing with these params (absent on data stored before it was tracked)
	clickedAt?: string
	// Set for the install attribution of the mobile apps, absent for the web's UTM capture
	provider?: 'appsflyer'
}

const STORAGE_KEY = 'mos-acquisition'

// Landing again with the very same params within this delay is a reload/back navigation, not a new click.
const SAME_LANDING_DELAY_MS = 30 * 60_000

const URL_PARAM_KEYS: { param: string; field: keyof Omit<AcquisitionData, 'referrer' | 'clickedAt' | 'provider'> }[] = [
	{ param: 'utm_source', field: 'utmSource' },
	{ param: 'utm_medium', field: 'utmMedium' },
	{ param: 'utm_campaign', field: 'utmCampaign' },
	{ param: 'utm_content', field: 'utmContent' },
	{ param: 'utm_term', field: 'utmTerm' },
	{ param: 'fbclid', field: 'fbclid' },
	{ param: 'gclid', field: 'gclid' },
	{ param: 'ttclid', field: 'ttclid' },
]

// Last-touch marketing attribution (same model as Meta/Google's own attribution): captured from the landing
// URL, persisted, and only replaced by a later landing that carries tracking params of its own - plain
// visits/navigations keep it - so revenue can eventually be joined against it independently of what
// Meta/Google's own dashboards report. Always goes through localStorage directly, not the
// StorageStrategy/Capacitor Preferences abstraction used elsewhere - the Capacitor WebView already has a
// real localStorage, and routing this through the native Preferences bridge would add latency for no benefit.
@Injectable({ providedIn: 'root' })
export class AcquisitionRepository {
	private readonly store = signal<AcquisitionData | null>(null)

	// Set when this page load is a new landing with tracking params, for the visit count (TrackingService)
	private newLanding: AcquisitionData | null = null

	constructor() {
		if (isServer) {
			return
		}

		const stored = this.readFromStorage()
		const captured = this.captureFromUrl()
		if (captured && !this.isSameLanding(stored, captured)) {
			this.store.set(captured)
			this.writeToStorage(captured)
			this.newLanding = captured
			return
		}

		this.store.set(stored)
	}

	get data(): AcquisitionData | null {
		return this.store()
	}

	get landing(): AcquisitionData | null {
		return this.newLanding
	}

	// Install attribution of the mobile apps (MobileAttributionService): replaces the stored campaign like a new
	// landing does, and goes out at registration through the same X-Acquisition header.
	setInstallAttribution(data: AcquisitionData) {
		this.store.set(data)
		this.writeToStorage(data)
	}

	// Out of the address bar once read (here, and by the analytics tags): not carried along by a shared or bookmarked
	// url. The router's own history state is kept as is.
	removeParamsFromUrl() {
		if (isServer) {
			return
		}

		const url = new URL(window.location.href)
		const params = URL_PARAM_KEYS.map(({ param }) => param).filter((param) => url.searchParams.has(param))
		if (params.length === 0) {
			return
		}

		params.forEach((param) => url.searchParams.delete(param))
		history.replaceState(history.state, '', url.pathname + url.search + url.hash)
	}

	private isSameLanding(stored: AcquisitionData | null, captured: AcquisitionData) {
		if (!stored?.clickedAt || Date.now() - new Date(stored.clickedAt).getTime() > SAME_LANDING_DELAY_MS) {
			return false
		}
		return URL_PARAM_KEYS.every(({ field }) => stored[field] === captured[field])
	}

	private readFromStorage(): AcquisitionData | null {
		try {
			const raw = localStorage.getItem(STORAGE_KEY)
			return raw ? (JSON.parse(raw) as AcquisitionData) : null
		} catch {
			return null
		}
	}

	private writeToStorage(data: AcquisitionData) {
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
		} catch {
			// Private browsing / storage disabled - the attribution only lives for this page load.
		}
	}

	private captureFromUrl(): AcquisitionData | null {
		const params = new URLSearchParams(window.location.search)
		const hasAnyParam = URL_PARAM_KEYS.some(({ param }) => params.has(param))
		if (!hasAnyParam) {
			return null
		}

		const data = { referrer: document.referrer || null, clickedAt: new Date().toISOString() } as AcquisitionData
		for (const { param, field } of URL_PARAM_KEYS) {
			data[field] = params.get(param)
		}
		return data
	}
}
